import { ActionPageKey } from 'enums/action-page-key';
import { useUtmParams } from 'kl-b2c-ui-kit';
import React, { FC, useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getActionsApiService, getApiService, getStorageService } from 'services/api.factory';
import { Participant } from 'types/participant';
import RegistrationDescription from './components/RegistrationDescription';
import RegistrationForm from './components/RegistrationForm';
import SpeakerDescription from './components/SpeakerDescription';
import SponsorDescription from './components/SponsorDescription';
import { PageConfig } from './config-mapper';
import { HttpResponse } from 'types';

import {
    CardImage,
    Column,
    ColumnWrapper,
    Container,
    DescriptionContainer,
    DescriptionTitle,
    PageTitle,
} from './styled';
import { useLocation } from 'react-router-dom';
import { DefaultTheme, withTheme } from 'styled-components';
import Loading from 'components/common/Loading';
import Success from 'components/common/Success';
import Error from 'components/common/Error';

export const DEFAULT_FORM_STATE: Omit<Participant, 'captchaCode'> = {
    firstName: '',
    lastName: '',
    topic: '',
    comments: '',
    company: '',
    email: '',
    phone: '',
    position: '',
    file: null,
    agreement: {
        marketingIsSelected: false,
        privacyPolicyIsSelected: false,
    },
};

type ActionPageProps = {
    pageKey: ActionPageKey;
    theme: DefaultTheme;
};

const ActionPage: FC<ActionPageProps> = ({ pageKey, theme }) => {
    const config = PageConfig[pageKey];
    const { t } = useTranslation('pages/action-page');
    const storageService = getStorageService();
    const apiService = getApiService(storageService);
    const actionsApiService = getActionsApiService(apiService);
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState(false);
    const [doi, setDoi] = useState(false);
    const [generalError, setGeneralError] = useState<{ title: string; description: string } | null>(null);

    const location = useLocation();

    useEffect(() => {
        reset(DEFAULT_FORM_STATE);
        clearErrors();
    }, [location]);

    const methods = useForm<Participant>();
    const { reset, clearErrors } = methods;

    const getPPmessage = (actionPageKey: ActionPageKey): string => {
        switch (actionPageKey) {
            case ActionPageKey.Papers:
                return t('pp-papers');
            case ActionPageKey.Sponsor:
                return t('pp-sponsor');

            default:
                return t('pp');
        }
    };

    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        setLoading(true);
        const utm = useUtmParams();

        try {
            const requestData: Participant = {
                ...(data as Participant),
                agreement: {
                    marketingIsSelected: data.agreement.marketingIsSelected || false,
                    marketingCheckBoxText: t('marketing'),
                    privacyPolicyIsSelected: true,
                    privacyPolicyCheckBoxText: getPPmessage(pageKey),
                },
                ...utm,
            };
            let response: HttpResponse<void>;
            switch (pageKey) {
                case ActionPageKey.Registration: {
                    response = await actionsApiService.register(requestData);
                    break;
                }
                case ActionPageKey.Sponsor: {
                    response = await actionsApiService.sponsor(requestData);
                    break;
                }
                case ActionPageKey.Papers: {
                    response = await actionsApiService.papers(requestData);
                    break;
                }
            }

            setLoading(false);
            if (response.status === 200) {
                setSuccess(true);
                reset(DEFAULT_FORM_STATE);
                clearErrors();
            } else if (response.status === 202) {
                setSuccess(true);
                setDoi(true);
                reset(DEFAULT_FORM_STATE);
                clearErrors();
            } else if (response.status === 207) {
                setGeneralError({
                    description: t('error-207'),
                    title: t('interest'),
                });
            } else {
                setGeneralError({
                    description: t('something-wrong'),
                    title: t('oops'),
                });
            }
        } catch (error) {
            setGeneralError({
                description: t('something-wrong'),
                title: t('oops'),
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (success || loading || generalError) {
            document.body.classList.add('is-modal-open');
        } else {
            document.body.classList.remove('is-modal-open');
        }
    }, [success, loading, generalError]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Container>
            <PageTitle variant={'h1'} fontWeight={'regular'}>
                {t(`${config.title}`)}
            </PageTitle>
            <ColumnWrapper>
                <Column flex width={1180}>
                    <CardImage src={config.image} alt="Page image" />

                    <DescriptionContainer>
                        <DescriptionTitle variant={'h2'} fontWeight={'regular'}>
                            {t(`${config.descriptionTitle}`)}
                        </DescriptionTitle>

                        {(() => {
                            // LAST <Column flex width={pageKey !== ActionPageKey.Papers ? 658 : 1180}>
                            switch (pageKey) {
                                case ActionPageKey.Registration:
                                    return <RegistrationDescription />;
                                case ActionPageKey.Sponsor:
                                    return <SponsorDescription />;
                                case ActionPageKey.Papers:
                                    return <SpeakerDescription />;
                            }
                        })()}
                    </DescriptionContainer>
                </Column>
                {(() => {
                    switch (pageKey) {
                        case ActionPageKey.Registration:
                        case ActionPageKey.Sponsor:
                            return null;
                        // return (
                        //     <Column>
                        //         <FormProvider {...methods}>
                        //             <RegistrationForm
                        //                 actionPageKey={pageKey}
                        //                 hidePosition={pageKey === ActionPageKey.Sponsor}
                        //                 submit={onSubmit}
                        //                 loading={loading}
                        //             />
                        //         </FormProvider>
                        //     </Column>
                        // );
                        case ActionPageKey.Papers:
                            return null;
                        // return (
                        //     <Column>
                        //         <FormProvider {...methods}>
                        //             <RegistrationForm
                        //                 actionPageKey={pageKey}
                        //                 submit={onSubmit}
                        //                 loading={loading}
                        //                 hasPresentation
                        //             />
                        //         </FormProvider>
                        //     </Column>
                        // );
                    }
                })()}
            </ColumnWrapper>
            {loading && <Loading label={t('data-sending')} />}
            {success && (
                <Success
                    color={theme.palette.text.secondary}
                    gradient={theme.palette.green}
                    close={() => setSuccess(false)}
                    gap={0}
                    additionalText={doi ? t('doi-text') : ''}
                />
            )}
            {generalError && (
                <Error
                    gap={0}
                    close={() => setGeneralError(null)}
                    title={generalError?.title}
                    text={generalError?.description}
                />
            )}
        </Container>
    );
};

export default withTheme(ActionPage);
